import { useColorMode } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useAtom } from 'jotai';
import {
  themeModeAtom,
  systemThemeAtom,
  systemThemeListenerAtom,
  isDarkAtom,
  themeEffectAtom,
  effectiveThemeAtom,
  darkModeAtom,
  toggleDarkModeAtom,
  setThemeModeAtom,
  ThemeMode
} from '../../atoms/shared/uiAtoms';

/**
 * 简化的主题模式 Hook
 * 使用新的简化主题管理系统，移除复杂的同步逻辑
 *
 * @returns {Object} 包含当前主题模式和主题相关工具的对象
 */
export const useThemeMode = () => {
  // 使用 Chakra UI 的颜色模式（为了兼容性保留）
  const { colorMode, toggleColorMode: chakraToggleColorMode } = useColorMode();

  // 使用简化的主题状态
  const [themeMode] = useAtom(themeModeAtom);
  const [systemTheme] = useAtom(systemThemeAtom);
  const [isDarkMode] = useAtom(isDarkAtom);
  const [effectiveTheme] = useAtom(effectiveThemeAtom);
  const [, setThemeMode] = useAtom(setThemeModeAtom);
  const [, toggleDarkMode] = useAtom(toggleDarkModeAtom);
  const [, applyThemeEffect] = useAtom(themeEffectAtom);
  const [, initSystemListener] = useAtom(systemThemeListenerAtom);

  // 初始化系统主题监听（只在组件挂载时执行一次）
  useEffect(() => {
    initSystemListener();
    // 初始应用主题
    applyThemeEffect({ type: 'apply' });
  }, [initSystemListener, applyThemeEffect]);

  // 同步 Chakra UI 模式（仅在必要时）
  useEffect(() => {
    if ((colorMode === 'dark') !== isDarkMode) {
      chakraToggleColorMode();
    }
  }, [isDarkMode, colorMode, chakraToggleColorMode]);

  // 简化的设置主题函数
  const setThemeModeAndSync = (newMode: ThemeMode) => {
    setThemeMode(newMode);
  };

  // 简化的切换主题函数
  const toggleColorMode = () => {
    toggleDarkMode();
  };

  return {
    // 当前主题模式
    themeMode,
    setThemeMode: setThemeModeAndSync,
    effectiveTheme,
    isDarkMode,
    colorMode: effectiveTheme, // 兼容旧代码

    // 系统主题信息
    systemTheme,
    isSystemDarkMode: systemTheme === 'dark',

    // 切换功能
    toggleColorMode,

    // 工具函数: 获取在不同主题下使用的值
    getThemedValue: <T>(lightValue: T, darkValue: T): T => {
      return effectiveTheme === 'dark' ? darkValue : lightValue;
    },
  };
};